import * as THREE from 'https://esm.sh/three@0.160.0';
import { RoundedBoxGeometry } from 'https://esm.sh/three@0.160.0/examples/jsm/geometries/RoundedBoxGeometry.js';
import { MarchingCubes } from 'https://esm.sh/three@0.160.0/examples/jsm/objects/MarchingCubes.js';
import { RoomEnvironment } from 'https://esm.sh/three@0.160.0/examples/jsm/environments/RoomEnvironment.js';
import { EffectComposer } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/OutputPass.js';
import { Reflector } from 'https://esm.sh/three@0.160.0/examples/jsm/objects/Reflector.js';

// ─── Mobile detection ──────────────────────────────────────────────────
// Mobile gets a stripped-down rendering path: no postprocessing,
// no Reflector, no caustic SpotLight, lower MarchingCubes resolution,
// simpler water material (no transmission), lower DPR, lower poly counts.
const IS_MOBILE =
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches) ||
  (typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));

let progress = 0;
let sectionEl = null;
let canvasContainer = null;

function trackScroll() {
  if (!sectionEl) {
    sectionEl = document.querySelector('.h2-root');
    if (!sectionEl) return;
  }
  const rect = sectionEl.getBoundingClientRect();
  const winH = window.innerHeight;
  const max = Math.max(1, sectionEl.offsetHeight - winH);
  progress = Math.min(1, Math.max(0, -rect.top / max));
  if (canvasContainer) {
    const inView = rect.bottom > -100 && rect.top < winH + 100;
    const vis = inView ? 'visible' : 'hidden';
    canvasContainer.style.visibility = vis;
    const overlay = document.querySelector('.h2-text-overlay');
    if (overlay) overlay.style.visibility = vis;
    const flash = document.querySelector('.h2-flash');
    if (flash) flash.style.visibility = vis;
    const splash = document.querySelector('.h2-splash');
    if (splash) splash.style.visibility = vis;
  }
}
window.addEventListener('scroll', trackScroll, { passive: true });
window.addEventListener('resize', trackScroll);

const BG = 0x04081a;
const BLUE = 0x142b77;
const BLUE_MID = 0x066ac1;
const BLUE_LIGHT = 0x9ad9ff;
const CYAN = 0x7be0ff;

const _texLoader = new THREE.TextureLoader();
_texLoader.crossOrigin = 'anonymous';
const LOGO_URL = 'https://drwater.store/cdn/shop/files/DR.WATER-White-Logo.webp?v=1769441826&width=1200';
let _drwaterLogoTex = null;
function getLogoTex() {
  if (!_drwaterLogoTex) {
    _drwaterLogoTex = _texLoader.load(LOGO_URL);
    _drwaterLogoTex.colorSpace = THREE.SRGBColorSpace;
    _drwaterLogoTex.anisotropy = IS_MOBILE ? 1 : 4;
  }
  return _drwaterLogoTex;
}

function makeCausticsTexture() {
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  const x = c.getContext('2d');
  x.fillStyle = '#000000'; x.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 35; i++) {
    const cx = Math.random() * 256, cy = Math.random() * 256;
    const r = 12 + Math.random() * 24;
    const grad = x.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0,   'rgba(255,255,255,0.55)');
    grad.addColorStop(0.4, 'rgba(180,220,255,0.18)');
    grad.addColorStop(1,   'rgba(0,0,0,0)');
    x.fillStyle = grad;
    x.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

function makeScreenTexture() {
  const c = document.createElement('canvas'); c.width = 512; c.height = 384;
  const x = c.getContext('2d');
  const lg = x.createLinearGradient(0, 0, 512, 384);
  lg.addColorStop(0, '#3aa0d4'); lg.addColorStop(0.4, '#1e5a8a');
  lg.addColorStop(0.7, '#0d2030'); lg.addColorStop(1, '#1a3a5a');
  x.fillStyle = lg; x.fillRect(0, 0, 512, 384);
  const rg = x.createRadialGradient(180, 110, 0, 180, 110, 240);
  rg.addColorStop(0, 'rgba(168,220,255,0.55)');
  rg.addColorStop(0.5, 'rgba(168,220,255,0.15)');
  rg.addColorStop(1, 'rgba(168,220,255,0)');
  x.fillStyle = rg; x.fillRect(0, 0, 512, 384);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function buildMachine() {
  const m = new THREE.Group();

  const cabinetWhite = new THREE.MeshPhysicalMaterial({
    color: 0xf4f4f2, metalness: 0.45, roughness: 0.30,
    clearcoat: IS_MOBILE ? 0 : 0.5, clearcoatRoughness: 0.15, envMapIntensity: 1.4
  });
  const glossBlack = new THREE.MeshPhysicalMaterial({
    color: 0x0a0a0a, metalness: 0.0, roughness: 0.04,
    clearcoat: IS_MOBILE ? 0 : 1.0, clearcoatRoughness: 0.03, envMapIntensity: 1.6
  });
  const brushedSteel  = new THREE.MeshPhysicalMaterial({ color: 0xc8c8c8, metalness: 0.95, roughness: 0.20, envMapIntensity: 1.1 });
  const knobAluminum  = new THREE.MeshPhysicalMaterial({ color: 0xd4d4d4, metalness: 0.95, roughness: 0.25, envMapIntensity: 1.3 });
  const knobCap       = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, metalness: 0.30, roughness: 0.55 });
  const screwMat      = new THREE.MeshStandardMaterial({ color: 0x303030, metalness: 0.60, roughness: 0.40 });
  const recessDark    = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, metalness: 0.10, roughness: 0.70 });
  const portBlue      = new THREE.MeshStandardMaterial({ color: 0x1e90ff, metalness: 0.30, roughness: 0.40 });
  const switchRed     = new THREE.MeshStandardMaterial({ color: 0xc0322d, metalness: 0.10, roughness: 0.50 });
  const rubber        = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, metalness: 0.00, roughness: 0.85 });
  const ledCyan = new THREE.MeshStandardMaterial({
    color: 0x000000, emissive: 0x00d4ff, emissiveIntensity: 2.0,
    toneMapped: false, roughness: 0.2
  });

  const matteSilicone = new THREE.MeshPhysicalMaterial({
    color: 0x1a1a1a, metalness: 0.05, roughness: 0.55,
    clearcoat: IS_MOBILE ? 0 : 0.3, clearcoatRoughness: 0.5, envMapIntensity: 0.7
  });

  const W = 1.52, D = 1.12;
  const halfW = W / 2, halfD = D / 2;

  // Cabinet — wedge profile via ExtrudeGeometry
  const profile = new THREE.Shape();
  profile.moveTo(-halfD, -0.76);
  profile.lineTo( halfD, -0.76);
  profile.lineTo( halfD,  0.52);
  profile.lineTo(-0.20,   0.76);
  profile.lineTo(-halfD,  0.76);
  profile.closePath();
  const cabinetGeom = new THREE.ExtrudeGeometry(profile, {
    depth: W, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.04,
    bevelSegments: IS_MOBILE ? 2 : 4, curveSegments: IS_MOBILE ? 6 : 12
  });
  cabinetGeom.translate(0, 0, -W / 2);
  cabinetGeom.rotateY(Math.PI / 2);
  const body = new THREE.Mesh(cabinetGeom, cabinetWhite);
  body.castShadow = !IS_MOBILE; body.receiveShadow = !IS_MOBILE;
  m.add(body);

  const panelW = 1.44, panelH = 1.20, panelT = 0.032;
  const panelCenterY = -0.12;
  const panelZ = halfD + panelT / 2 + 0.001;
  const panel = new THREE.Mesh(new RoundedBoxGeometry(panelW, panelH, panelT, IS_MOBILE ? 3 : 6, 0.10), glossBlack);
  panel.position.set(0, panelCenterY, panelZ);
  m.add(panel);
  const panelFrontZ = panelZ + panelT / 2;
  const panelTop = panelCenterY + panelH / 2;
  const panelBot = panelCenterY - panelH / 2;

  // Phillips screws (skip on mobile to save geometry)
  if (!IS_MOBILE) {
    const screwGeom = new THREE.CylinderGeometry(0.024, 0.024, 0.012, 14);
    const placeScrew = (x, y, z) => {
      const s = new THREE.Mesh(screwGeom, screwMat);
      s.rotation.x = Math.PI / 2; s.position.set(x, y, z);
      m.add(s);
    };
    const sIn = 0.13;
    placeScrew(-panelW / 2 + sIn,  panelTop - sIn, panelFrontZ + 0.001);
    placeScrew( panelW / 2 - sIn,  panelTop - sIn, panelFrontZ + 0.001);
    placeScrew(-panelW / 2 + sIn,  panelBot + sIn, panelFrontZ + 0.001);
    placeScrew( panelW / 2 - sIn,  panelBot + sIn, panelFrontZ + 0.001);
    const backFaceZ = -halfD - 0.001;
    placeScrew(-halfW + sIn,  0.76 - sIn, backFaceZ);
    placeScrew( halfW - sIn,  0.76 - sIn, backFaceZ);
    placeScrew(-halfW + sIn, -0.76 + sIn, backFaceZ);
    placeScrew( halfW - sIn, -0.76 + sIn, backFaceZ);
  }
  const backFaceZ = -halfD - 0.001;

  const screenY = panelCenterY + panelH * 0.18;
  const screenTex = makeScreenTexture();
  const screen = new THREE.Mesh(
    new THREE.PlaneGeometry(0.72, 0.52),
    new THREE.MeshStandardMaterial({
      map: screenTex, emissiveMap: screenTex, emissive: 0xffffff,
      emissiveIntensity: 0.7, toneMapped: false, roughness: 0.4
    })
  );
  screen.position.set(0, screenY, panelFrontZ + 0.002);
  m.add(screen);
  const screenLogo = new THREE.Mesh(
    new THREE.PlaneGeometry(0.50, 0.36),
    new THREE.MeshBasicMaterial({
      map: getLogoTex(), transparent: true, opacity: 0.45,
      alphaTest: 0.05, depthWrite: false, toneMapped: false
    })
  );
  screenLogo.position.set(0, screenY, panelFrontZ + 0.010);
  m.add(screenLogo);

  // Knobs — lower radial on mobile
  const knobSeg = IS_MOBILE ? 24 : 64;
  const knobGeom    = new THREE.CylinderGeometry(0.072, 0.072, 0.088, knobSeg);
  const knobCapGeom = new THREE.CylinderGeometry(0.040, 0.040, 0.012, knobSeg);
  const knobY = panelCenterY - panelH * 0.06;
  const knobOffset = 0.30;
  for (const x of [-knobOffset, knobOffset]) {
    const k = new THREE.Mesh(knobGeom, knobAluminum);
    k.rotation.x = Math.PI / 2;
    k.position.set(x, knobY, panelFrontZ + 0.044);
    m.add(k);
    const cap = new THREE.Mesh(knobCapGeom, knobCap);
    cap.rotation.x = Math.PI / 2;
    cap.position.set(x, knobY, panelFrontZ + 0.094);
    m.add(cap);
  }

  const wordmark = new THREE.Mesh(
    new THREE.PlaneGeometry(0.40, 0.10),
    new THREE.MeshBasicMaterial({
      map: getLogoTex(), transparent: true, opacity: 0.6,
      alphaTest: 0.05, toneMapped: false, depthWrite: false
    })
  );
  wordmark.position.set(0, panelCenterY - panelH * 0.40, panelFrontZ + 0.002);
  m.add(wordmark);

  // Casters (lower poly on mobile)
  const stemH = 0.10, wheelR = 0.088;
  const stemGeom  = new THREE.CylinderGeometry(0.030, 0.030, stemH, IS_MOBILE ? 8 : 12);
  const wheelGeom = new THREE.TorusGeometry(wheelR, 0.040, IS_MOBILE ? 6 : 10, IS_MOBILE ? 14 : 24);
  const casterY = -0.76 - stemH / 2;
  [
    [-halfW + 0.14,  halfD - 0.14, true ],
    [ halfW - 0.14,  halfD - 0.14, true ],
    [-halfW + 0.14, -halfD + 0.14, false],
    [ halfW - 0.14, -halfD + 0.14, false]
  ].forEach(([x, z, locking]) => {
    const stem = new THREE.Mesh(stemGeom, brushedSteel);
    stem.position.set(x, casterY, z);
    m.add(stem);
    const wheel = new THREE.Mesh(wheelGeom, rubber);
    wheel.rotation.y = Math.PI / 2;
    wheel.position.set(x, casterY - stemH / 2 - wheelR + 0.04, z);
    m.add(wheel);
    if (locking) {
      const lever = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.024, 0.012), brushedSteel);
      lever.position.set(x, casterY - stemH / 2, z + 0.06);
      m.add(lever);
    }
  });

  // Folding handle
  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.20, 0.022, IS_MOBILE ? 6 : 10, IS_MOBILE ? 16 : 28, Math.PI), brushedSteel);
  handle.rotation.x = -Math.PI / 2;
  handle.position.set(0, 0.78, -0.38);
  m.add(handle);
  for (const x of [-0.20, 0.20]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.026, 0.040, IS_MOBILE ? 8 : 12), brushedSteel);
    post.position.set(x, 0.78, -0.38);
    m.add(post);
  }

  const sideHandle = new THREE.Mesh(new RoundedBoxGeometry(0.28, 0.14, 0.06, IS_MOBILE ? 2 : 4, 0.04), recessDark);
  sideHandle.position.set(halfW + 0.001, 0.18, 0.10);
  sideHandle.rotation.y = Math.PI / 2;
  m.add(sideHandle);

  // Vents — skip perforation texture on mobile (use flat dark plane)
  const ventMat = IS_MOBILE
    ? new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.85 })
    : new THREE.MeshStandardMaterial({
        color: 0x141414, roughness: 0.85, metalness: 0.0,
        map: (() => {
          const c = document.createElement('canvas'); c.width = 256; c.height = 320;
          const x = c.getContext('2d');
          x.fillStyle = '#0c0c0c'; x.fillRect(0, 0, 256, 320);
          x.fillStyle = '#000000';
          for (let i = 7; i < 256; i += 14) for (let j = 7; j < 320; j += 14) {
            x.beginPath(); x.arc(i, j, 2.5, 0, Math.PI * 2); x.fill();
          }
          const t = new THREE.CanvasTexture(c);
          t.wrapS = t.wrapT = THREE.RepeatWrapping;
          return t;
        })()
      });
  for (const z of [0.28, -0.28]) {
    const vent = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.42), ventMat);
    vent.position.set(halfW + 0.002, -0.12, z); vent.rotation.y = Math.PI / 2;
    m.add(vent);
  }

  const pureFitting = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.020, IS_MOBILE ? 10 : 16), brushedSteel);
  pureFitting.position.set(halfW - 0.18, 0.78, -0.36);
  m.add(pureFitting);

  // Back panel (skip detailed elements on mobile — they're rarely visible)
  if (!IS_MOBILE) {
    const fanCanvas = document.createElement('canvas'); fanCanvas.width = 256; fanCanvas.height = 256;
    const fc = fanCanvas.getContext('2d');
    fc.fillStyle = '#0c0c0c'; fc.fillRect(0, 0, 256, 256);
    fc.translate(128, 128);
    fc.strokeStyle = '#5a5a5a'; fc.lineWidth = 4;
    for (let i = 0; i < 8; i++) { fc.beginPath(); fc.moveTo(0, 0); fc.lineTo(Math.cos(i * Math.PI / 4) * 110, Math.sin(i * Math.PI / 4) * 110); fc.stroke(); }
    fc.lineWidth = 2;
    for (const r of [40, 70, 100]) { fc.beginPath(); fc.arc(0, 0, r, 0, Math.PI * 2); fc.stroke(); }
    fc.fillStyle = '#3a3a3a'; fc.beginPath(); fc.arc(0, 0, 16, 0, Math.PI * 2); fc.fill();
    const fanTex = new THREE.CanvasTexture(fanCanvas);
    const fan = new THREE.Mesh(new THREE.CircleGeometry(0.30, 32), new THREE.MeshStandardMaterial({ map: fanTex, roughness: 0.7, metalness: 0.2 }));
    fan.position.set(0, 0.04, backFaceZ); fan.rotation.y = Math.PI;
    m.add(fan);
    const usbGeom = new THREE.BoxGeometry(0.084, 0.030, 0.012);
    for (const x of [0.30, 0.44]) {
      const u = new THREE.Mesh(usbGeom, recessDark);
      u.position.set(x, 0.56, backFaceZ - 0.006);
      m.add(u);
    }
    const outlet = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.13, 0.026), recessDark);
    outlet.position.set(0.34, -0.54, backFaceZ - 0.013); m.add(outlet);
    const rocker = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.045, 0.014), switchRed);
    rocker.position.set(0.34, -0.52, backFaceZ - 0.022); m.add(rocker);
    const fuse = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.006, 12), recessDark);
    fuse.rotation.x = Math.PI / 2; fuse.position.set(0.18, -0.54, backFaceZ - 0.002); m.add(fuse);
    const portGeom = new THREE.CylinderGeometry(0.020, 0.020, 0.014, 12);
    [-0.36, -0.22, -0.08, 0.06].forEach((x) => {
      const port = new THREE.Mesh(portGeom, portBlue);
      port.rotation.x = Math.PI / 2; port.position.set(x, -0.70, backFaceZ - 0.005);
      m.add(port);
    });
  }

  const ledFront = new THREE.Mesh(new THREE.BoxGeometry(W * 0.94, 0.012, 0.020), ledCyan);
  ledFront.position.set(0, -0.78, halfD + 0.011);
  m.add(ledFront);
  const ledSideGeom = new THREE.BoxGeometry(0.020, 0.012, D * 0.7);
  for (const x of [-halfW - 0.011, halfW + 0.011]) {
    const sled = new THREE.Mesh(ledSideGeom, ledCyan);
    sled.position.set(x, -0.78, 0);
    m.add(sled);
  }
  const ledLight = new THREE.PointLight(0x00d4ff, IS_MOBILE ? 0.6 : 1.25, 1.6, 2.0);
  ledLight.position.set(0, -0.82, halfD + 0.05);
  m.add(ledLight);

  // Hose + wand (lower poly on mobile)
  const attachX = halfW + 0.01, attachY = -0.26, attachZ = halfD - 0.34;
  const attachFitting = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.048, 0.034, IS_MOBILE ? 12 : 18), brushedSteel);
  attachFitting.rotation.z = Math.PI / 2;
  attachFitting.position.set(attachX, attachY, attachZ);
  m.add(attachFitting);
  const hosePts = [
    new THREE.Vector3(attachX, attachY, attachZ),
    new THREE.Vector3(attachX + 0.30, attachY - 0.05, attachZ + 0.10),
    new THREE.Vector3(attachX + 0.46, attachY - 0.18, attachZ - 0.06),
    new THREE.Vector3(attachX + 0.50, attachY - 0.34, attachZ - 0.22),
    new THREE.Vector3(attachX + 0.40, attachY - 0.50, attachZ - 0.32),
    new THREE.Vector3(attachX + 0.20, attachY - 0.60, attachZ - 0.30),
    new THREE.Vector3(attachX + 0.10, attachY - 0.70, attachZ - 0.20),
    new THREE.Vector3(attachX + 0.16, attachY - 0.82, attachZ - 0.05)
  ];
  const hoseCurve = new THREE.CatmullRomCurve3(hosePts, false, 'catmullrom', 0.5);
  const hose = new THREE.Mesh(new THREE.TubeGeometry(hoseCurve, IS_MOBILE ? 32 : 64, 0.032, IS_MOBILE ? 6 : 8, false), matteSilicone);
  m.add(hose);
  const wand = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.032, 0.48, IS_MOBILE ? 10 : 14), brushedSteel);
  const tip = hosePts[hosePts.length - 1];
  wand.position.set(tip.x + 0.02, tip.y - 0.20, tip.z);
  m.add(wand);
  const foot = new THREE.Mesh(new THREE.SphereGeometry(0.044, IS_MOBILE ? 10 : 16, IS_MOBILE ? 10 : 16), matteSilicone);
  foot.position.set(tip.x + 0.02, tip.y - 0.46, tip.z);
  m.add(foot);

  return m;
}

function buildMolecule() {
  const g = new THREE.Group();
  const segs = IS_MOBILE ? 24 : 48;
  const atomMat = (color, roughness = 0.18) => IS_MOBILE
    ? new THREE.MeshStandardMaterial({ color, roughness, metalness: 0, emissive: color, emissiveIntensity: 0.15 })
    : new THREE.MeshPhysicalMaterial({ color, roughness, metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.08, transmission: 0.25, thickness: 1, sheen: 1, sheenColor: color });
  const O = new THREE.Mesh(new THREE.SphereGeometry(0.55, segs, segs), atomMat(BLUE_MID, 0.22));
  g.add(O);
  const angle = (104.5 / 2) * (Math.PI / 180);
  const bondLen = 1.0;
  const H1pos = new THREE.Vector3(Math.sin(angle) * bondLen, -Math.cos(angle) * bondLen, 0);
  const H2pos = new THREE.Vector3(-Math.sin(angle) * bondLen, -Math.cos(angle) * bondLen, 0);
  const H1 = new THREE.Mesh(new THREE.SphereGeometry(0.32, segs, segs), atomMat(BLUE_LIGHT)); H1.position.copy(H1pos); g.add(H1);
  const H2 = new THREE.Mesh(new THREE.SphereGeometry(0.32, segs, segs), atomMat(BLUE_LIGHT)); H2.position.copy(H2pos); g.add(H2);
  const bondMat = new THREE.MeshStandardMaterial({ color: BLUE, roughness: 0.55, metalness: 0.15 });
  function bond(from, to) {
    const dir = new THREE.Vector3().subVectors(to, from);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    const c = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, len, IS_MOBILE ? 10 : 16), bondMat);
    c.position.copy(mid); c.quaternion.copy(q); g.add(c);
  }
  bond(new THREE.Vector3(0,0,0), H1pos);
  bond(new THREE.Vector3(0,0,0), H2pos);
  return g;
}

const VignetteShader = {
  uniforms: { tDiffuse: { value: null }, offset: { value: 1.10 }, darkness: { value: 0.55 } },
  vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform sampler2D tDiffuse; uniform float offset; uniform float darkness; varying vec2 vUv;
    void main() {
      vec4 t = texture2D(tDiffuse, vUv);
      vec2 d = (vUv - 0.5) * vec2(offset);
      float v = clamp(1.0 - dot(d, d) * darkness, 0.0, 1.0);
      gl_FragColor = vec4(t.rgb * v, t.a);
    }`
};
const ChromaticShader = {
  uniforms: { tDiffuse: { value: null }, amount: { value: 0.0006 } },
  vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform sampler2D tDiffuse; uniform float amount; varying vec2 vUv;
    void main() {
      vec2 dir = vUv - 0.5;
      float r = texture2D(tDiffuse, vUv + dir * amount).r;
      vec4 g = texture2D(tDiffuse, vUv);
      float b = texture2D(tDiffuse, vUv - dir * amount).b;
      gl_FragColor = vec4(r, g.g, b, g.a);
    }`
};

function init(container) {
  if (container.parentElement !== document.body) document.body.appendChild(container);
  const overlayEl = document.querySelector('.h2-text-overlay');
  if (overlayEl && overlayEl.parentElement !== document.body) document.body.appendChild(overlayEl);
  const flashEl = document.querySelector('.h2-flash');
  if (flashEl && flashEl.parentElement !== document.body) document.body.appendChild(flashEl);
  const splashEl = document.querySelector('.h2-splash');
  if (splashEl && splashEl.parentElement !== document.body) document.body.appendChild(splashEl);
  canvasContainer = container;

  const canvas = container.querySelector('canvas');
  if (!canvas) return;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: !IS_MOBILE, alpha: false, powerPreference: 'high-performance' });
  renderer.setClearColor(BG, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_MOBILE ? 1.0 : 1.5));
  renderer.shadowMap.enabled = !IS_MOBILE;
  if (renderer.shadowMap.enabled) renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(BG, 9, 24);

  // PMREM env — needed for chrome reflections; cheap on mobile (small RT)
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 60);
  camera.position.set(0, 0, 3.0);

  scene.add(new THREE.HemisphereLight(0x18223a, 0x000000, 0.20));
  const key = new THREE.DirectionalLight(0xffffff, 2.0);
  key.position.set(2, 3, 2);
  if (!IS_MOBILE) {
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    Object.assign(key.shadow.camera, { near: 0.5, far: 25, left: -5, right: 5, top: 5, bottom: -5 });
    key.shadow.bias = -0.0008;
  }
  scene.add(key);
  const rim = new THREE.DirectionalLight(0xa8d4ff, 2.5);
  rim.position.set(-1, 2, -2); scene.add(rim);
  const fill = new THREE.DirectionalLight(0xffe8d4, 0.5);
  fill.position.set(-1, -1, 1); scene.add(fill);
  if (!IS_MOBILE) {
    const showroomSpot = new THREE.SpotLight(0xa8d4ff, 1.5, 14, 0.4, 1.0, 1.0);
    showroomSpot.position.set(-3, 4, 2);
    showroomSpot.target.position.set(0, 0, 0);
    scene.add(showroomSpot); scene.add(showroomSpot.target);
    const cyanL = new THREE.PointLight(CYAN, 5, 14); cyanL.position.set(-3.5, 1.5, 2); scene.add(cyanL);
    const navyL = new THREE.PointLight(BLUE_MID, 3.5, 12); navyL.position.set(3.5, -1, -2); scene.add(navyL);
  }

  // Caustic SpotLight — DESKTOP ONLY (heavy: SpotLight.map projection)
  let causticTex = null;
  if (!IS_MOBILE) {
    causticTex = makeCausticsTexture();
    const causticSpot = new THREE.SpotLight(0xa8d4ff, 1.2, 14, 0.6, 0.8, 1.0);
    causticSpot.position.set(1, 4, 1);
    causticSpot.target.position.set(0, 0, 0);
    causticSpot.map = causticTex;
    scene.add(causticSpot); scene.add(causticSpot.target);
  }

  // Reflective floor — DESKTOP ONLY (heavy: extra full-scene render pass)
  if (!IS_MOBILE) {
    const reflector = new Reflector(new THREE.PlaneGeometry(8, 8), {
      textureWidth: 1024, textureHeight: 1024, color: 0x0a0e1a
    });
    reflector.rotation.x = -Math.PI / 2;
    reflector.position.y = -1.0;
    scene.add(reflector);
  } else {
    // Mobile: simple dark floor disc with a soft contact-shadow gradient on top
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(8, 32),
      new THREE.MeshBasicMaterial({ color: 0x06080f })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.0;
    scene.add(floor);
  }

  // Water material — desktop uses transmission; mobile uses cheaper translucent
  const waterMat = IS_MOBILE
    ? new THREE.MeshStandardMaterial({
        color: 0xa8d4ff, roughness: 0.05, metalness: 0,
        transparent: true, opacity: 0.7, depthWrite: false,
        emissive: 0x1a3a5a, emissiveIntensity: 0.15
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0xe8f3ff, roughness: 0.04, transmission: 0.98, thickness: 1.4, ior: 1.38,
        clearcoat: 1.0, clearcoatRoughness: 0.02, attenuationColor: 0xa8d4ff, attenuationDistance: 6.0,
        iridescence: 0.25, iridescenceIOR: 1.3, iridescenceThicknessRange: [120, 580],
        envMapIntensity: 1.6, transparent: true
      });

  // MarchingCubes — much lower res on mobile (32 → 16)
  const RES = IS_MOBILE ? 16 : 32;
  const droplet = new MarchingCubes(RES, waterMat, true, true, IS_MOBILE ? 30000 : 90000);
  droplet.scale.setScalar(2.0); droplet.isolation = 80;
  scene.add(droplet);

  // Bubbles — fewer + lower poly on mobile
  const bubbleGroup = new THREE.Group();
  const N = IS_MOBILE ? 10 : 26;
  const bubbleSegs = IS_MOBILE ? 10 : 18;
  const bubbles = [];
  for (let i = 0; i < N; i++) {
    const r = 0.07 + Math.random() * 0.18;
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, bubbleSegs, bubbleSegs), waterMat);
    const u = 2 * Math.random() - 1;
    const phi = Math.random() * Math.PI * 2;
    const sx = Math.sqrt(1 - u * u) * Math.cos(phi);
    const sy = u;
    const sz = Math.sqrt(1 - u * u) * Math.sin(phi);
    m.userData = {
      sx, sy, sz,
      dirX: sx * (1.2 + Math.random() * 1.4),
      dirY: sy * (0.6 + Math.random() * 1.0),
      dirZ: sz * (1.2 + Math.random() * 1.4) + (0.4 + Math.random() * 1.0),
      delay: Math.random() * 0.05,
      wobble: Math.random() * Math.PI * 2,
      baseScale: 0.5 + Math.random() * 0.4
    };
    m.position.set(sx * 1.45, sy * 1.45, sz * 1.45);
    m.scale.setScalar(m.userData.baseScale);
    bubbleGroup.add(m); bubbles.push(m);
  }
  scene.add(bubbleGroup);

  function makeContactShadow() {
    const c = document.createElement('canvas'); c.width = 256; c.height = 256;
    const x = c.getContext('2d');
    const grad = x.createRadialGradient(128, 128, 16, 128, 128, 128);
    grad.addColorStop(0, 'rgba(0,0,0,0.65)');
    grad.addColorStop(0.45, 'rgba(0,0,0,0.30)');
    grad.addColorStop(1, 'rgba(0,0,0,0.0)');
    x.fillStyle = grad; x.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  }
  const contactShadow = new THREE.Mesh(
    new THREE.PlaneGeometry(3.2, 3.2),
    new THREE.MeshBasicMaterial({ map: makeContactShadow(), transparent: true, depthWrite: false, opacity: 0.55 })
  );
  contactShadow.rotation.x = -Math.PI / 2;
  contactShadow.position.y = -0.99;
  contactShadow.renderOrder = 1;
  contactShadow.visible = false;
  scene.add(contactShadow);

  const machine = buildMachine();
  machine.position.y = -5;
  machine.visible = false;
  scene.add(machine);

  const haloMat = new THREE.MeshBasicMaterial({ color: BLUE_MID, transparent: true, opacity: 0, side: THREE.BackSide, depthWrite: false });
  const halo = new THREE.Mesh(new THREE.SphereGeometry(4.5, IS_MOBILE ? 16 : 32, IS_MOBILE ? 16 : 32), haloMat);
  halo.position.set(0, 0, -2.2); scene.add(halo);

  const molecule = buildMolecule();
  molecule.position.set(0, 0, -1.5); molecule.scale.setScalar(0); molecule.visible = false;
  scene.add(molecule);

  // ── Postprocessing — DESKTOP ONLY ─────────────────────────────────────
  let composer = null;
  let bloomPass = null;
  if (!IS_MOBILE) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.4, 0.4, 0.85
    );
    composer.addPass(bloomPass);
    composer.addPass(new ShaderPass(VignetteShader));
    composer.addPass(new ShaderPass(ChromaticShader));
    composer.addPass(new OutputPass());
  }

  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    if (composer) composer.setSize(w, h);
    if (bloomPass) bloomPass.resolution.set(w, h);
    camera.aspect = w / h; camera.updateProjectionMatrix();
  }
  resize(); window.addEventListener('resize', resize);

  const overlay = document.querySelector('.h2-text-overlay');
  const flash = document.querySelector('.h2-flash');
  const splash = document.querySelector('.h2-splash');

  trackScroll();

  let smooth = 0, t = 0;

  function tick() {
    t += 0.016;
    smooth += (progress - smooth) * 0.08;
    const p = smooth;

    if (causticTex) {
      causticTex.offset.x = (t * 0.02) % 1;
      causticTex.offset.y = (t * 0.013) % 1;
    }

    const shrinkT = Math.max(0, Math.min(1, p / 0.20));
    const dropScale = 2.0 * (1 - shrinkT);
    droplet.visible = dropScale > 0.06;
    if (droplet.visible) {
      droplet.reset();
      const T = t * 0.42;
      const s = 0.5;
      droplet.addBall(0.5 + Math.sin(T * 0.70) * 0.22, 0.5 + Math.cos(T * 0.55) * 0.15, 0.5, s, 12);
      droplet.addBall(0.5 + Math.cos(T * 0.50 + 1.2) * 0.20, 0.5 + Math.sin(T * 0.73 + 0.8) * 0.16, 0.5 + Math.sin(T * 0.30) * 0.05, s * 0.85, 12);
      droplet.addBall(0.5 + Math.sin(T * 0.90 + 2.1) * 0.15, 0.5 + Math.cos(T * 0.60 + 1.5) * 0.18, 0.5 + Math.cos(T * 0.40 + 0.7) * 0.04, s * 0.70, 12);
      droplet.addBall(0.5 + Math.cos(T * 1.10 + 0.5) * 0.11, 0.5 + Math.sin(T * 0.85 + 2.3) * 0.11, 0.5 + Math.sin(T * 0.60) * 0.03, s * 0.55, 12);
      droplet.update();
      droplet.scale.setScalar(dropScale);
      droplet.rotation.y = t * 0.06;
    }

    bubbleGroup.visible = p < 0.50;
    if (bubbleGroup.visible) {
      const surfRadius = 1.45 * (1 - shrinkT);
      bubbles.forEach((b) => {
        const peelP = Math.max(0, Math.min(1, (p - b.userData.delay) / 0.32));
        b.position.set(
          b.userData.sx * surfRadius + b.userData.dirX * peelP * 2.4 + Math.sin(t * 0.6 + b.userData.wobble) * 0.04 * peelP,
          b.userData.sy * surfRadius + b.userData.dirY * peelP * 1.8,
          b.userData.sz * surfRadius + b.userData.dirZ * peelP * 5.5
        );
        b.scale.setScalar(b.userData.baseScale + peelP * 0.5);
      });
    }

    const machVis = p > 0.42 && p < 0.85;
    machine.visible = machVis;
    contactShadow.visible = machVis;
    if (machVis) {
      const mIn = Math.max(0, Math.min(1, (p - 0.42) / 0.10));
      const mOut = Math.max(0, (p - 0.70) / 0.15);
      machine.position.y = -5 + mIn * 5 + mOut * 6 + Math.sin(t) * 0.04;
      machine.rotation.y = Math.PI * (-0.1 + (p - 0.42) * 0.7);
      machine.rotation.x = -p * 0.06;
      machine.scale.setScalar((0.6 + mIn * 0.4 - mOut * 0.2) * 1.5);
      haloMat.opacity = mIn * 0.20 * (1 - mOut);
      halo.scale.setScalar(1 + mIn * 0.6);
      contactShadow.position.x = machine.position.x;
      contactShadow.position.z = machine.position.z;
      contactShadow.position.y = -0.99;
      contactShadow.scale.setScalar(0.7 + mIn * 0.6);
      contactShadow.material.opacity = mIn * 0.55 * (1 - mOut * 0.7);
    } else {
      haloMat.opacity = 0;
    }

    molecule.visible = p > 0.72;
    if (molecule.visible) {
      const mp = Math.max(0, Math.min(1, (p - 0.72) / 0.12));
      molecule.scale.setScalar(mp * 0.95);
      molecule.rotation.y = t * 0.3;
      molecule.rotation.x = Math.sin(t * 0.4) * 0.2;
      molecule.position.y = -0.8 + mp * 1.6;
    }

    let camZ;
    if (p < 0.20)      camZ = 3.0 + (p / 0.20) * 1.5;
    else if (p < 0.42) camZ = 4.5 + ((p - 0.20) / 0.22) * 0.8;
    else if (p < 0.52) camZ = 5.3 - ((p - 0.42) / 0.10) * 1.3;
    else if (p < 0.70) camZ = 4.0 + ((p - 0.52) / 0.18) * 1.2;
    else               camZ = 5.2 + ((p - 0.70) / 0.30) * 1.4;
    camera.position.z = camZ;
    camera.position.y = 0.0 - p * 0.3;
    camera.lookAt(0, 0, 0);

    // Mobile: direct render. Desktop: through composer with full pipeline.
    if (composer) composer.render();
    else renderer.render(scene, camera);

    if (overlay) {
      const op = Math.max(0, Math.min(1, (p - 0.50) / 0.15));
      overlay.style.opacity = (op * 0.60).toFixed(3);
    }
    if (flash) {
      const fp = Math.max(0, 1 - Math.abs(p - 0.42) / 0.03);
      flash.style.opacity = (fp * 0.55).toFixed(3);
    }
    if (splash) {
      const sp = Math.max(0, 1 - Math.abs(p - 0.30) / 0.06);
      splash.style.opacity = sp.toFixed(3);
      splash.classList.toggle('is-on', sp > 0.05);
    }

    requestAnimationFrame(tick);
  }
  tick();
}

const io = new IntersectionObserver((entries) => {
  entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
}, { threshold: 0.18 });

const statIO = new IntersectionObserver((entries) => {
  entries.forEach(e => {
    if (!e.isIntersecting) return;
    const el = e.target;
    const target = parseFloat(el.dataset.num);
    const suffix = el.dataset.suffix || '';
    const decimals = String(el.dataset.num).includes('.') ? 1 : 0;
    const dur = 1500;
    const start = performance.now();
    function step(now) {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = target * eased;
      const display = decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString();
      el.textContent = display + suffix;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    statIO.unobserve(el);
  });
}, { threshold: 0.4 });

function boot() {
  const c = document.querySelector('[data-h2bath-canvas]');
  if (c) init(c);
  document.querySelectorAll('.h2-fadeup').forEach(el => io.observe(el));
  document.querySelectorAll('.h2-stat-num').forEach(el => statIO.observe(el));
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
